import React from 'react';
import Seo from '../components/global/Seo';

/**
 * Public SMS Opt-In Disclosure page — https://nuvesystem.com/sms-opt-in
 *
 * Purpose: provides wireless carriers and The Campaign Registry (TCR) with a
 * publicly verifiable record of how end-users consent to receive SMS messages
 * from NuVe System (operated by NUVE SYSTEM LLC). Screenshots are served from
 * the public/ folder at the site root and referenced by absolute URL.
 */

const SCREENSHOTS = {
  phoneEntry: process.env.PUBLIC_URL + '/01_phone_entry.png',
  consentUnchecked: process.env.PUBLIC_URL + '/02_consent_unchecked.png',
  consentChecked: process.env.PUBLIC_URL + '/03_consent_checked.png',
  confirmationSms: process.env.PUBLIC_URL + '/04_confirmation_sms.png',
};

class SmsOptIn extends React.Component {
  handleBack = () => {
    if (this.props.history) {
      this.props.history.goBack();
    } else {
      window.history.back();
    }
  };

  render() {
    return (
      <div style={containerStyle}>
        <Seo
          title="SMS Opt-In Disclosure"
          description="NuVe System SMS opt-in disclosure: how customers consent to receive text messages, the types of messages sent, and how to opt out."
          path="/sms-opt-in"
        />
        {/* --- FIXED APP TOOLBAR --- */}
        <div style={toolbarStyle}>
          <button onClick={this.handleBack} style={iconButtonStyle}>
            ←
          </button>
          <div style={toolbarTitleStyle}>SMS Opt-In Disclosure</div>
          <div style={{ width: '40px' }}></div>
        </div>

        <div style={cardStyle}>
          {/* Header */}
          <header style={headerStyle}>
            <h1 style={titleStyle}>
              NuVe System — SMS Program &amp; Opt-In Disclosure
            </h1>
            <p style={{ ...dateStyle, fontStyle: 'italic' }}>
              Last Updated: June 12, 2026
            </p>
          </header>

          <div style={contentStyle}>
            {/* 1. Program overview + required disclosures */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>1. Program Overview</h2>
              <p>
                NuVe System (operated by NUVE SYSTEM LLC) is a membership and
                loyalty platform used by independently operated business
                locations. NuVe System sends two categories of SMS messages:
              </p>
              <ul style={listStyle}>
                <li>
                  <strong>Phone Verification (required):</strong> one-time
                  verification codes (OTP) used to confirm the customer's
                  identity and provide access to membership features.
                </li>
                <li>
                  <strong>Member Benefits &amp; Promotions (optional):</strong>{' '}
                  recurring promotional offers, loyalty rewards, special event
                  announcements, and business updates — sent{' '}
                  <strong>only</strong> to customers who separately and
                  expressly opt in.
                </li>
              </ul>
              <div style={importantNoticeStyle}>
                <strong>
                  Message frequency varies. Message and data rates may apply.
                </strong>{' '}
                Reply <strong>STOP</strong> to opt out of promotional messages
                at any time. Reply <strong>HELP</strong> for help, or contact
                info@nuvesystem.com. Consent to promotional messages is{' '}
                <strong>not</strong> a condition of membership or of using NuVe
                System. Mobile phone numbers and SMS opt-in data are never
                shared with, sold to, or rented to third parties or affiliates
                for marketing purposes.
              </div>
            </section>

            {/* 2. How opt-in happens */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>2. How End-Users Opt In</h2>
              <p>
                Consent is captured digitally, directly from the customer.
                During membership check-in or enrollment, after entering their
                phone number, the customer is shown a consent screen with an
                optional <strong>Member Benefits &amp; Promotions</strong>{' '}
                checkbox that is <strong>unchecked by default</strong> and
                separate from the required Phone Verification consent. The
                customer must affirmatively check the box to receive promotional
                messages, and can continue without doing so. Each opt-in is
                recorded with a UTC timestamp and location and stored as proof
                of consent. Promotional messages are sent only to customers
                whose record shows an affirmative marketing opt-in.
              </p>
            </section>

            {/* 3. Screenshots of the live opt-in flow */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>3. Screenshots of the Opt-In Process</h2>
              <p>
                The following screenshots are captured from the live NuVe System
                check-in flow, in order. They show that the marketing consent is
                unchecked by default, the full disclosure presented to the
                customer, the affirmative opt-in, and the confirmation message
                the customer receives. Customer phone numbers are masked for
                privacy.
              </p>

              <div style={screenshotGridStyle}>
                <figure style={screenshotFigureStyle}>
                  <img
                    src={SCREENSHOTS.phoneEntry}
                    alt="NuVe System check-in screen where the customer enters their phone number"
                    style={screenshotImgStyle}
                    loading="lazy"
                  />
                  <figcaption style={screenshotCaptionStyle}>
                    Step 1 — Membership check-in / phone number entry.
                  </figcaption>
                </figure>

                <figure style={screenshotFigureStyle}>
                  <img
                    src={SCREENSHOTS.consentUnchecked}
                    alt="Consent screen showing the Member Benefits and Promotions marketing checkbox unchecked by default, with the full SMS disclosure"
                    style={screenshotImgStyle}
                    loading="lazy"
                  />
                  <figcaption style={screenshotCaptionStyle}>
                    Step 2 — Marketing consent shown{' '}
                    <strong>unchecked by default</strong>, with the full
                    disclosure. The customer can continue without accepting it.
                  </figcaption>
                </figure>

                <figure style={screenshotFigureStyle}>
                  <img
                    src={SCREENSHOTS.consentChecked}
                    alt="Consent screen showing the Member Benefits and Promotions marketing checkbox affirmatively checked by the customer"
                    style={screenshotImgStyle}
                    loading="lazy"
                  />
                  <figcaption style={screenshotCaptionStyle}>
                    Step 3 — Customer <strong>affirmatively checks</strong> the
                    marketing consent box to opt in.
                  </figcaption>
                </figure>

                <figure style={screenshotFigureStyle}>
                  <img
                    src={SCREENSHOTS.confirmationSms}
                    alt="Opt-in confirmation text message received by the customer after opting in to promotional messages from NuVe System"
                    style={screenshotImgStyle}
                    loading="lazy"
                  />
                  <figcaption style={screenshotCaptionStyle}>
                    Step 4 — Opt-in confirmation message received by SMS after
                    opting in.
                  </figcaption>
                </figure>
              </div>
            </section>

            {/* 4. Sample messages */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>4. Example Messages</h2>
              <p>
                <strong>Verification (required consent):</strong>
              </p>
              <div style={smsBubbleStyle}>
                Your NuVe System verification code is: 123456. This code expires
                in 5 minutes.
              </div>
              <p>
                <strong>Promotional (optional consent only):</strong>
              </p>
              <div style={smsBubbleStyle}>
                NuVe System: Hi {'{first_name}'}, bonus play points are waiting
                for you at {'{location_name}'} this weekend! Reply STOP to opt
                out, HELP for help.
              </div>
            </section>

            {/* 5. Opt-out & help */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>5. Opt-Out and Support</h2>
              <ul style={listStyle}>
                <li>
                  <strong>STOP:</strong> Reply STOP to any promotional message
                  to cancel promotional messages. You will receive one
                  confirmation message and no further promotional messages.
                </li>
                <li>
                  <strong>HELP:</strong> Reply HELP to any message, or contact
                  info@nuvesystem.com for assistance.
                </li>
              </ul>
            </section>

            {/* 6. Links */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>6. Policies</h2>
              <p>
                <a href="/privacy-policy" style={linkStyle}>
                  Privacy Policy
                </a>
                {' · '}
                <a href="/terms-and-conditions" style={linkStyle}>
                  Terms and Conditions
                </a>
              </p>
              <div style={contactBoxStyle}>
                NUVE SYSTEM LLC
                <br />
                5900 Balcones Drive STE 100
                <br />
                Austin, TX 78731
                <br />
                United States
                <br />
                <br />
                info@nuvesystem.com
                <br />
                (346) 461-5222
              </div>
            </section>
          </div>
        </div>
      </div>
    );
  }
}

// ===== Page styles =====

const dateStyle = {
  color: '#777',
  fontSize: '0.95rem',
  fontWeight: '400',
};

const contentStyle = {
  textAlign: 'left',
};

const sectionStyle = {
  marginBottom: '35px',
};

const headingStyle = {
  color: '#001665',
  fontSize: '1.3rem',
  marginBottom: '15px',
  fontWeight: '700',
};

const listStyle = {
  paddingLeft: '25px',
  marginBottom: '15px',
};

const importantNoticeStyle = {
  backgroundColor: '#f8f9ff',
  borderLeft: '4px solid #001665',
  padding: '15px 20px',
  marginTop: '15px',
  fontSize: '0.95rem',
  fontStyle: 'italic',
};

const contactBoxStyle = {
  backgroundColor: '#f4f4f4',
  padding: '20px',
  borderRadius: '8px',
  marginTop: '10px',
  display: 'inline-block',
  minWidth: '250px',
};

const linkStyle = {
  color: '#1a237e',
  fontWeight: '600',
};

const toolbarStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  height: '60px',
  backgroundColor: '#001665',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 15px',
  zIndex: 1000,
  boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
};

const iconButtonStyle = {
  backgroundColor: 'transparent',
  color: 'white',
  border: 'none',
  fontSize: '24px',
  cursor: 'pointer',
  padding: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const toolbarTitleStyle = {
  color: 'white',
  fontSize: '18px',
  fontWeight: '600',
};

const containerStyle = {
  backgroundColor: '#ffffff',
  minHeight: '100vh',
  paddingTop: '80px',
  paddingBottom: '40px',
  paddingLeft: '15px',
  paddingRight: '15px',
  display: 'flex',
  justifyContent: 'center',
  fontFamily: '"Segoe UI", Roboto, Helvetica, Arial, sans-serif',
};

const cardStyle = {
  backgroundColor: '#ffffff',
  maxWidth: '900px',
  width: '100%',
  padding: '30px 20px',
  borderRadius: '12px',
  color: '#333',
  lineHeight: '1.7',
};

const headerStyle = {
  borderBottom: '3px solid #001665',
  marginBottom: '30px',
  paddingBottom: '15px',
};

const titleStyle = {
  color: '#001665',
  fontSize: '1.8rem',
  margin: '0 0 10px 0',
};

const screenshotGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  gap: '20px',
  marginTop: '20px',
};

const screenshotFigureStyle = {
  margin: 0,
  border: '1px solid #c5cae9',
  borderRadius: '12px',
  overflow: 'hidden',
  backgroundColor: '#f7f8fc',
};

const screenshotImgStyle = {
  display: 'block',
  width: '100%',
  height: 'auto',
};

const screenshotCaptionStyle = {
  padding: '12px 14px',
  fontSize: '0.85rem',
  color: '#333',
  lineHeight: '1.45',
  margin: 0,
};

const smsBubbleStyle = {
  backgroundColor: '#e8eaf6',
  border: '1px solid #c5cae9',
  borderRadius: '14px',
  padding: '12px 16px',
  maxWidth: '440px',
  fontSize: '0.92rem',
  color: '#0a1128',
  marginBottom: '15px',
};

export default SmsOptIn;
